/**
 * CreateThreadModal — create or edit a Thread record for the current project.
 *
 * Opens via: CustomEvent('open-create-thread-modal', { detail }) handled by
 * CreateThreadModalHost (mounted in OutlineView), or directly via props from
 * ThreadsSection.
 *
 * Event detail:
 *   projectId         — project record ID (defaults to wf.zoomProjectId)
 *   phaseId           — optional; preselects the Phase picker (create mode)
 *   threadId          — optional; opens in EDIT mode for that thread
 *
 * Create flow is TWO-STEP (Round 16): step 1 picks the Type (card grid), step 2 is a
 * form that depends on the type. Submittals/RFIs are construction-support concepts — their
 * cards appear ONLY when the thread is being added from the Construction Support phase. For
 * those types the user then chooses to RECORD A NEW submittal/RFI (→ the contract picker →
 * the existing AddSubmittal/AddRfi modal, which owns the record + its own thread) or to start
 * a general discussion thread of that type. Edit mode skips the picker and shows the form.
 *
 * Phase attachment: a thread attaches to ONE phase via the 'Phase' single-preference link.
 */

import { useState, useEffect } from 'react'
import { wf } from '../outline/state.js'
import { ctx } from '../outline/ctx.js'
import { createThread, updateThread, getThreadById, getProjectThreads, constructionSupportPhaseId, syncOpeningActivityNote } from '../services/threads.js'
import { getProjectContracts } from '../services/bidCampaigns.js'
import { getTeamMembers, contactIdForMember } from '../services/members.js'
import Select from './Select.jsx'
import { getRidingFor } from '../services/passengers.js'

// Thread type values — verified against Airtable singleSelect options on Threads table (tblXlbRZkUldgBHkc).
// Update this list if Airtable options change.
export const THREAD_TYPES = [
  'Schedule and Progress',
  'Design Approvals',
  'Design Direction',
  'Specialty Design',
  'Submittals',
  'RFIs',
  'PCOs and Change Orders',
  'Permitting Items',
  'Contractor Items',
  'Inspector Items',
  'Owner Items',
  'Architect Items',
  'Tendering Items',
  'Misc. Items',
]

const TYPE_OPTIONS = THREAD_TYPES.map(t => ({ value: t, label: t }))

// Types that represent a tracked construction RECORD (not just a discussion). These are only
// offered from the Construction Support phase, and lead to "record new vs. discussion".
const SPECIAL_TYPES = { 'Submittals': 'submittal', 'RFIs': 'rfi' }
const RECORD_NOUN = { submittal: 'submittal', rfi: 'RFI' }

export default function CreateThreadModal({ projectId, onClose, onCreated, thread = null, prefillPhaseId = '' }) {
  const isEdit = !!thread
  const tf = thread?.fields || {}

  const csPhaseId = constructionSupportPhaseId(ctx.state?.phases)
  // Submittal/RFI threads may only be initiated from the Construction Support phase.
  const startedInCS = !isEdit && !!csPhaseId && prefillPhaseId === csPhaseId

  const [step, setStep]     = useState(isEdit ? 'form' : 'type')  // type | subchoice | contract | form
  const [recordKind, setRecordKind] = useState(null)              // 'submittal' | 'rfi' (special types)
  const [contracts, setContracts] = useState(null)                // null = loading, [] = none

  const [title,    setTitle]    = useState(tf['Title'] || '')
  const [type,     setType]     = useState(tf['Type'] || '')
  const [desc,     setDesc]     = useState(tf['Description'] || '')
  const [phaseId,  setPhaseId]  = useState((tf['Phase'] || [])[0] || prefillPhaseId || '')
  // PASSENGERS (2026-09-09, docs/PASSENGERS.md): every new thread starts in someone's court — the
  // daily triage gate is keyed on it, so a thread with nobody in court would never be triaged.
  // Defaults to the person creating it. Airtable stores a CONTACT, so the member is mapped on save.
  const [courtMemberId, setCourtMemberId] = useState(wf.currentMemberId || '')
  const [teamMembers, setTeamMembers] = useState(null)
  useEffect(() => { if (!isEdit) getTeamMembers().then(setTeamMembers).catch(() => setTeamMembers([])) }, [isEdit])
  const courtOptions = (teamMembers || []).map(m => ({ value: m.id, label: m.name }))
  // A thread riding a milestone takes that milestone's phase (Matt, 2026-10-26); the picker is only
  // for a thread riding nothing. undefined = checking, null = rides nothing, else { milestoneName, phaseId, phaseName }.
  const [ride, setRide] = useState(isEdit ? undefined : null)
  useEffect(() => {
    if (!isEdit) return
    getRidingFor([thread.id]).then(r => setRide((r.byThread || {})[thread.id] || null)).catch(() => setRide(null))
  }, [isEdit, thread && thread.id])
  const [submitting, setSub]    = useState(false)
  const [error,    setError]    = useState(null)

  // Phase picker options — the project's phases in schedule order
  const phaseOptions = [{ value: '', label: '— none —' }].concat(
    (ctx.state?.phases || []).map(p => ({
      value: p.id,
      label: p.fields['Phase Name'] || p.fields['Name'] || 'Phase',
    }))
  )

  // Type cards — hide Submittals/RFIs unless we're in the Construction Support phase.
  const typeCards = THREAD_TYPES.filter(t => (SPECIAL_TYPES[t] ? startedInCS : true))

  useEffect(() => {
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = '' }
  }, [])

  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  // ── Step navigation ────────────────────────────────────────────────────────
  function pickType(t) {
    setError(null)
    setType(t)
    if (SPECIAL_TYPES[t]) { setRecordKind(SPECIAL_TYPES[t]); setStep('subchoice') }
    else setStep('form')
  }
  function chooseRecordNew() {
    setStep('contract')
    if (!contracts) getProjectContracts(projectId).then(setContracts).catch(() => setContracts([]))
  }
  function chooseDiscussion() {
    // type is already set to Submittals/RFIs; phase stays the CS phase
    setStep('form')
  }
  function pickContract(contractId) {
    const evt = recordKind === 'rfi' ? 'open-add-rfi-modal' : 'open-add-submittal-modal'
    window.dispatchEvent(new CustomEvent(evt, { detail: { contractId, projectId } }))
    onClose()
  }
  function back() {
    setError(null)
    if (step === 'form') { setStep(recordKind ? 'subchoice' : 'type') }
    else if (step === 'contract') { setStep('subchoice') }
    else if (step === 'subchoice') { setStep('type'); setRecordKind(null); setType('') }
  }

  async function handleSubmit() {
    if (!title.trim() || !type) return
    const memberId = wf.currentMemberId
    setSub(true)
    setError(null)
    try {
      let res
      if (isEdit) {
        const fields = { 'Title': title.trim(), 'Type': type, 'Description': desc.trim() }
        const hadPhase = (tf['Phase'] || []).length > 0
        if (ride && ride.phaseId) fields['Phase'] = [ride.phaseId]
        else if (phaseId || hadPhase) fields['Phase'] = phaseId ? [phaseId] : []
        res = await updateThread(thread.id, fields)
        // Keep the opening activity's note synced with the description (they show the same text; the
        // 'Thread Opened' activity surfaces it in the outline activity log).
        await syncOpeningActivityNote(thread.id, desc.trim())
        if (ctx.threadActCache) ctx.threadActCache[thread.id] = undefined
      } else {
        res = await createThread(
          projectId, title.trim(), type,
          null, desc.trim() || undefined, memberId,
          { phaseId: phaseId || undefined },
        )
        // Court holder (see courtMemberId above). Best effort: a failed mapping leaves the thread
        // with no court, where the tower's list on the Tower tab catches it.
        if (res && res.id && courtMemberId) {
          try { const cid = await contactIdForMember(courtMemberId); if (cid) await updateThread(res.id, { 'Ball In Court': [cid] }) } catch (_) { /* tower list catches it */ }
        }
      }
      if (res.error) {
        const ae = res.error
        throw new Error(typeof ae === 'string' ? ae : (ae.message || ae.type || (isEdit ? 'Failed to update thread' : 'Failed to create thread')))
      }
      // Refetch the project threads so the new/edited thread appears immediately (with its correct
      // open status). Setting the cache to undefined alone doesn't reload — the phase Threads node's
      // lazy-load effect only fires on collapse/project change, not on cache reset.
      try { ctx.threadCache = await getProjectThreads(projectId) } catch { ctx.threadCache = undefined }
      // A thread mounted under a submittal/RFI (CaThreadMount) reads its own cache — bust it on edit.
      if (isEdit && thread?.id && ctx.caThreadCache) ctx.caThreadCache[thread.id] = undefined
      window.dispatchEvent(new Event('outline-render'))
      if (onCreated) onCreated(res)
      else onClose()
    } catch (ex) {
      setError(ex.message || (isEdit ? 'Failed to update thread' : 'Failed to create thread'))
      setSub(false)
    }
  }

  const canSubmit = title.trim() && type && !submitting && (isEdit || !!courtMemberId)

  // ── Styles ───────────────────────────────────────────────────────────────
  const labelStyle = { display: 'block', fontFamily: '"DM Mono",monospace', fontSize: 10, color: 'var(--aj-text-mid)', letterSpacing: '0.04em', marginBottom: 4, textTransform: 'uppercase' }
  const inputStyle = { width: '100%', padding: '7px 10px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: 'var(--aj-text)', background: 'var(--aj-bg-subtle)', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, boxSizing: 'border-box', outline: 'none' }
  const card = { textAlign: 'left', padding: '11px 13px', borderRadius: 6, border: '0.5px solid var(--aj-border-mid)', background: 'var(--aj-bg-subtle)', cursor: 'pointer', fontFamily: '"DM Sans",sans-serif', fontSize: 12.5, color: 'var(--aj-text)', fontWeight: 600 }
  const cardSub = { fontFamily: '"DM Sans",sans-serif', fontSize: 11, fontWeight: 400, color: 'var(--aj-text-mid)', marginTop: 4, lineHeight: 1.4 }

  const headerTitle =
    step === 'type'      ? 'New Thread'
    : step === 'subchoice' ? ('New ' + type + ' thread')
    : step === 'contract'  ? ('Record a new ' + RECORD_NOUN[recordKind])
    : isEdit               ? 'Edit Thread'
    : ('New ' + (type || '') + ' thread')

  return (
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 600, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
    >
      <div data-modal-id="CreateThreadModal" style={{ background: 'var(--aj-bg-elevated)', border: '0.5px solid var(--aj-border)', borderRadius: 8, boxShadow: '0 12px 36px rgba(0,0,0,0.16)', width: 440, padding: '20px 20px 16px', maxHeight: 'calc(100vh - 40px)', overflowY: 'auto' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {step !== 'type' && !isEdit && (
              <button onClick={back} title="Back" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--aj-text-mid)', fontSize: 15, lineHeight: 1, padding: 0 }}>‹</button>
            )}
            <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 13, fontWeight: 600, color: 'var(--aj-text)' }}>{headerTitle}</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--aj-text-mid)', fontSize: 16, lineHeight: 1, padding: 2 }}>×</button>
        </div>

        {/* ── Step 1: pick type ─────────────────────────────────────────────── */}
        {step === 'type' && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            {typeCards.map(t => (
              <button key={t} onClick={() => pickType(t)} style={card}>{t}</button>
            ))}
          </div>
        )}

        {/* ── Step 2a: record-new vs discussion (Submittals / RFIs) ─────────── */}
        {step === 'subchoice' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <button onClick={chooseRecordNew} style={card}>
              Record a new {RECORD_NOUN[recordKind]}
              <div style={cardSub}>Opens the Add {RECORD_NOUN[recordKind] === 'rfi' ? 'RFI' : 'Submittal'} form — the {RECORD_NOUN[recordKind]} record and its tracking thread are created together.</div>
            </button>
            <button onClick={chooseDiscussion} style={card}>
              General {type.toLowerCase()} thread
              <div style={cardSub}>A general discussion thread about {type.toLowerCase()} — not tied to a specific {RECORD_NOUN[recordKind]} record or its review log.</div>
            </button>
          </div>
        )}

        {/* ── Step 2b: choose a contract (record-new path) ──────────────────── */}
        {step === 'contract' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <label style={labelStyle}>Which contract is this {RECORD_NOUN[recordKind]} under?</label>
            {contracts === null && (
              <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: 'var(--aj-text-mid)' }}>Loading contracts…</div>
            )}
            {Array.isArray(contracts) && contracts.length === 0 && (
              <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: '#c0392b' }}>
                No construction contracts on this project yet. Add one from the Construction branch first.
              </div>
            )}
            {Array.isArray(contracts) && contracts.map(c => (
              <button key={c.id} onClick={() => pickContract(c.id)} style={card}>
                {c.contractor_name || 'Construction Contract'}
                <div style={cardSub}>{[c.contract_type, c.scope].filter(Boolean).join(' · ') || 'Contract'}</div>
              </button>
            ))}
          </div>
        )}

        {/* ── Form (normal type, discussion, or edit) ───────────────────────── */}
        {step === 'form' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div>
              <label style={labelStyle}>Title <span style={{ color: '#c0392b' }}>*</span></label>
              <input type="text" style={inputStyle} value={title} onChange={e => setTitle(e.target.value)} placeholder="Thread title…" autoFocus />
            </div>

            {/* Type — a dropdown when editing; otherwise fixed from step 1 */}
            {isEdit ? (
              <div>
                <label style={labelStyle}>Type <span style={{ color: '#c0392b' }}>*</span></label>
                <Select value={type} onChange={setType} options={TYPE_OPTIONS} placeholder="Select type…" style={{ width: '100%', fontSize: 12 }} />
              </div>
            ) : (
              <div>
                <label style={labelStyle}>Type</label>
                <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: 'var(--aj-text)' }}>{type}</div>
              </div>
            )}

            {!isEdit && (
              <div>
                <label style={labelStyle}>Whose court is it in? <span style={{ color: '#c0392b' }}>*</span></label>
                <Select value={courtMemberId} onChange={setCourtMemberId} options={courtOptions} placeholder={teamMembers === null ? 'Loading team…' : 'Pick a team member…'} style={{ width: '100%', fontSize: 12 }} />
                <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 11, color: 'var(--aj-text-mid)', marginTop: 4 }}>They triage it daily until it closes or rides a milestone.</div>
              </div>
            )}

            <div>
              <label style={labelStyle}>Phase</label>
              {ride ? (
                <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: 'var(--aj-text)', padding: '7px 10px', borderRadius: 4, background: 'var(--aj-bg-subtle)', border: '0.5px solid var(--aj-border)' }}>
                  {ride.phaseName || 'The milestone’s phase'}
                  <div style={{ fontSize: 11, color: 'var(--aj-text-mid)', marginTop: 2 }}>Set by the milestone it rides: {ride.milestoneName || 'its milestone'}. Move the thread to change it.</div>
                </div>
              ) : (
                <Select value={phaseId} onChange={setPhaseId} options={phaseOptions} placeholder={ride === undefined ? 'Checking…' : 'Select phase…'} disabled={ride === undefined} style={{ width: '100%', fontSize: 12 }} />
              )}
            </div>

            <div>
              <label style={labelStyle}>Description</label>
              <textarea style={{ ...inputStyle, resize: 'vertical', minHeight: 60 }} value={desc} onChange={e => setDesc(e.target.value)} placeholder="Optional description or context…" />
            </div>

            {error && <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 11, color: '#c0392b' }}>{error}</div>}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
              <button onClick={onClose} style={{ padding: '6px 14px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, background: 'none', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, cursor: 'pointer', color: 'var(--aj-text)' }}>Cancel</button>
              <button onClick={handleSubmit} disabled={!canSubmit} style={{ padding: '6px 16px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, fontWeight: 600, background: canSubmit ? '#07272D' : 'var(--aj-bg-muted)', color: canSubmit ? '#fff' : 'var(--aj-text-mid)', border: 'none', borderRadius: 4, cursor: canSubmit ? 'pointer' : 'default', transition: 'background 0.12s' }}>
                {submitting ? (isEdit ? 'Saving…' : 'Creating…') : (isEdit ? 'Save Changes' : 'Create Thread')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

/**
 * CreateThreadModalHost — global event host, mounted once in OutlineView.
 * Listens for 'open-create-thread-modal' and renders the modal with the
 * resolved thread (edit mode) or phase-derived prefills.
 */
export function CreateThreadModalHost() {
  const [cfg, setCfg] = useState(null)

  useEffect(() => {
    function onOpen(e) {
      const d = e.detail || {}
      const projectId = d.projectId || wf.zoomProjectId
      if (!projectId) return
      if (d.threadId) {
        const cached = Array.isArray(ctx.threadCache)
          ? ctx.threadCache.find(t => t.id === d.threadId)
          : null
        if (cached) {
          setCfg({ projectId, thread: cached, prefillPhaseId: '' })
        } else {
          getThreadById(d.threadId).then(rec => {
            if (rec && rec.fields) setCfg({ projectId, thread: rec, prefillPhaseId: '' })
          })
        }
        return
      }
      setCfg({ projectId, thread: null, prefillPhaseId: d.phaseId || '' })
    }
    window.addEventListener('open-create-thread-modal', onOpen)
    return () => window.removeEventListener('open-create-thread-modal', onOpen)
  }, [])

  if (!cfg) return null
  return (
    <CreateThreadModal
      projectId={cfg.projectId}
      thread={cfg.thread}
      prefillPhaseId={cfg.prefillPhaseId}
      onClose={() => setCfg(null)}
      onCreated={() => setCfg(null)}
    />
  )
}
